import React, { useEffect, useMemo, useState } from 'react';
import { getKitchenCategories, saveKitchenCategories } from '../../services/api';
import { formatMoney } from '../../utils/formatters';

/* Иконки рисуем штрихом вместо эмодзи — см. Navbar.jsx, откуда взят паттерн. */
const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const CloseIcon = () => <Icon><path d="M19.0068 5L5.00684 19" /><path d="M19 19L5 5" /></Icon>;

/* Раньше принадлежность группы к кухне или бару была захардкожена в коде под
   одну организацию, и всё незнакомое выбрасывалось из анализа молча. Здесь
   владелец видит все группы из iiko и раскладывает их сам; автоопределение
   лишь предлагает первый вариант. */
const CategorySettingsModal = ({ onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [types, setTypes] = useState([]);
  const [changes, setChanges] = useState({});
  const [filter, setFilter] = useState('');
  const [onlyAuto, setOnlyAuto] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getKitchenCategories();
        if (cancelled) return;
        setGroups(data.groups || []);
        setTypes(data.types || []);
      } catch (error) {
        console.error('Не удалось загрузить категории:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => {
    const query = filter.trim().toLowerCase();
    return groups.filter((group) => {
      if (onlyAuto && !group.auto) return false;
      if (query && !group.name.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [groups, filter, onlyAuto]);

  const typeOf = (group) => changes[group.name] ?? group.dish_type;
  const changedCount = Object.keys(changes).length;

  const handleChange = (group, value) => {
    setChanges((prev) => {
      const next = { ...prev };
      if (value === group.dish_type) delete next[group.name];
      else next[group.name] = value;
      return next;
    });
  };

  const handleSave = async () => {
    if (!changedCount) return;
    setSaving(true);
    try {
      await saveKitchenCategories(changes);
      onSaved?.();
    } catch (error) {
      console.error('Не удалось сохранить категории:', error);
      alert('Не удалось сохранить категории');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ui-modal" onClick={onClose}>
      <div className="ui-modal-box kt-categories-box" onClick={(e) => e.stopPropagation()}>
        <div className="ui-modal-head">
          <h2>Категории групп блюд</h2>
          <button className="ui-btn-ghost" onClick={onClose}><CloseIcon /></button>
        </div>

        <p className="kt-panel-hint">
          Фудкост считается только по кухне и бару. Товары и услуги видны в структуре выручки,
          но в процент не входят. «Не учитывать» полностью убирает группу из раздела.
        </p>

        <div className="kt-categories-controls">
          <input
            type="text"
            placeholder="Поиск группы…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <label className="kt-checkbox">
            <input
              type="checkbox"
              checked={onlyAuto}
              onChange={(e) => setOnlyAuto(e.target.checked)}
            />
            Только определённые автоматически
          </label>
        </div>

        {loading ? (
          <div className="kt-empty">Загрузка…</div>
        ) : (
          <div className="kt-categories-list">
            {visible.map((group) => (
              <div className="kt-category-row" key={group.name}>
                <div className="kt-category-info">
                  <div className="kt-category-name">{group.name}</div>
                  <div className="kt-category-meta">
                    {formatMoney(group.revenue)} за 30 дней
                    {group.auto && <span className="kt-auto-badge">авто</span>}
                  </div>
                </div>
                <select
                  value={typeOf(group)}
                  onChange={(e) => handleChange(group, e.target.value)}
                >
                  {types.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
            ))}
            {!visible.length && <div className="kt-empty">Ничего не найдено</div>}
          </div>
        )}

        <div className="ui-modal-actions">
          <button className="ui-btn-ghost" onClick={onClose}>Отмена</button>
          <button className="ui-btn-primary" onClick={handleSave} disabled={!changedCount || saving}>
            {saving ? 'Сохранение…' : `Сохранить${changedCount ? ` (${changedCount})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategorySettingsModal;
